import { Plus, Target, Handshake, HeartPulse, Users, Eye, EyeOff, Ban } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import {
  FilterBar,
  FilterClearButton,
  FilterSearch,
  FilterSelect,
  PageToolbar,
  SegmentTabs,
  ToolbarIconButton,
} from '@/components/ui/page-toolbar'
import type { CloserLeadsFilters } from '@/hooks/useCloserLeads'

const viewTabs = [
  { id: 'all' as const, label: 'Em Prospecção', icon: Target },
  { id: 'converted' as const, label: 'Convertidos', icon: Handshake },
  { id: 'nurturing' as const, label: 'Nutrição', icon: HeartPulse },
  { id: 'outro' as const, label: 'Outro comercial', icon: Ban },
  { id: 'mine' as const, label: 'Meus Clientes', icon: Users },
]

interface CloserToolbarProps {
  filters: CloserLeadsFilters
  closers?: string[]
  showCloserFilter?: boolean
  onNewLead: () => void
  metricsExpanded: boolean
  onToggleMetrics: () => void
}

function applyView(filters: CloserLeadsFilters, tabId: (typeof viewTabs)[number]['id']) {
  const { setActiveView, setFilterStatus, setActiveTab } = filters
  if (tabId === 'all') {
    setActiveView('all')
    setFilterStatus('')
    setActiveTab('all')
    return
  }
  if (tabId === 'converted') {
    setActiveView('converted')
    setFilterStatus('CONVERTIDO')
    setActiveTab('all')
    return
  }
  if (tabId === 'nurturing') {
    setActiveView('nurturing')
    setFilterStatus('')
    setActiveTab('all')
    return
  }
  if (tabId === 'outro') {
    setActiveView('outro')
    setFilterStatus('')
    setActiveTab('all')
    return
  }
  setActiveTab('mine')
  setActiveView('none')
}

function currentViewTab(activeView: string, activeTab: string): (typeof viewTabs)[number]['id'] {
  if (activeView === 'converted' || activeView === 'nurturing' || activeView === 'outro') return activeView
  if (activeTab === 'mine') return 'mine'
  return 'all'
}

export function CloserToolbar({
  filters,
  closers = [],
  showCloserFilter = false,
  onNewLead,
  metricsExpanded,
  onToggleMetrics,
}: CloserToolbarProps) {
  const {
    search, setSearch,
    filterStatus, setFilterStatus,
    filterTemp, setFilterTemp,
    closerFilter, setCloserFilter,
    activeView, activeTab,
  } = filters

  const currentTab = currentViewTab(activeView, activeTab)
  const hasFilters = Boolean(filterStatus || filterTemp || (showCloserFilter && closerFilter !== 'all'))

  return (
    <PageToolbar
      navigation={
        <SegmentTabs
          tabs={viewTabs}
          value={currentTab}
          onChange={(id) => applyView(filters, id)}
          ariaLabel='Visão da carteira'
        />
      }
      controls={
        <>
          <ToolbarIconButton
            active={metricsExpanded}
            onClick={onToggleMetrics}
            title={metricsExpanded ? 'Ocultar resumo' : 'Mostrar resumo'}
            aria-label={metricsExpanded ? 'Ocultar resumo' : 'Mostrar resumo'}
          >
            {metricsExpanded ? <Eye size={16} /> : <EyeOff size={16} />}
          </ToolbarIconButton>
          <Button variant='primary' size='sm' icon={<Plus size={14} />} onClick={onNewLead}>
            Novo Lead
          </Button>
        </>
      }
      filters={
        <FilterBar>
          <FilterSearch
            value={search}
            onChange={setSearch}
            placeholder='Buscar lead, empresa, contato...'
            ariaLabel='Buscar leads'
          />
          <FilterSelect
            value={filterStatus}
            onChange={setFilterStatus}
            title='Status'
          >
            <option value=''>Todos status</option>
            <option value='NOVO'>Novo</option>
            <option value='EM_CONTATO'>Em Contato</option>
            <option value='QUALIFICADO'>Qualificado</option>
            <option value='AGUARDANDO'>Aguardando</option>
          </FilterSelect>
          <FilterSelect
            value={filterTemp}
            onChange={setFilterTemp}
            title='Temperatura'
          >
            <option value=''>Todas temperaturas</option>
            <option value='hot'>Quente</option>
            <option value='warm'>Morno</option>
            <option value='cold'>Frio</option>
          </FilterSelect>
          {showCloserFilter && (
            <FilterSelect
              value={closerFilter}
              onChange={setCloserFilter}
              title='Closer'
              ariaLabel='Filtrar por closer'
            >
              <option value='all'>Todos os closers</option>
              {closers.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </FilterSelect>
          )}
          {hasFilters && (
            <FilterClearButton onClick={() => { setFilterStatus(''); setFilterTemp(''); setCloserFilter('all') }} />
          )}
        </FilterBar>
      }
    />
  )
}

export default CloserToolbar
